import { api } from '@/lib/http-client';
import type { QueryParams } from '@/types/api';
import type {
  BorradoConversacionResultado,
  ConversationFiltros,
  DetalleConversacion,
  EnviarImagenRequest,
  EnviarMensajeRequest,
  ListaConversaciones,
  ListaMensajes,
  ListMessagesParams,
  MensajeChat,
} from '@/types/conversations';

/**
 * Módulo 5 — Inbox: conversaciones y mensajes.
 *
 * Rutas reales (`backend/src/modules/conversations/conversations.routes.ts`), todas bajo
 * `authenticate` + `requireTenant()`: las ve cualquier miembro del negocio (el AGENTE_HUMANO
 * atiende el inbox) y el aislamiento entre negocios lo garantiza el backend (un id de otro
 * tenant responde 404, no 403).
 *
 * Dos cosas del contrato que no se pueden adivinar:
 *  - el listado se pagina por `limite`/`offset` (máximo 100) y el historial por **cursor**
 *    (`antes`/`antesId` → `siguienteCursor`, máximo 200): son mecanismos distintos a propósito;
 *  - `botActivo` viaja como string `'true' | 'false'` (el schema es
 *    `z.enum(['true', 'false'])`, no `z.coerce.boolean()`), así que se convierte aquí.
 *
 * Escribir: el handoff (pausar/reanudar el bot) es del módulo 4 → `@/api/ai-engine`; las
 * **respuestas manuales** del asesor sí son de este módulo (`sendConversationMessage` para texto y
 * `sendConversationImage` para una foto) porque son parte de la conversación, no del motor.
 */

/** Filtros de pantalla → query params, sin mandar los que no van (el backend valida rangos). */
export function conversationQueryFrom(filtros: ConversationFiltros = {}): QueryParams {
  const query: QueryParams = {};

  if (filtros.channel) query.channel = filtros.channel;
  if (filtros.estadoLead) query.estadoLead = filtros.estadoLead;
  if (filtros.estadoLeadOrigen) query.estadoLeadOrigen = filtros.estadoLeadOrigen;
  if (filtros.asistenteActivo) query.asistenteActivo = filtros.asistenteActivo;
  // `false` es un filtro de verdad ("el bot está pausado"), no la ausencia de filtro.
  if (filtros.botActivo !== undefined) query.botActivo = filtros.botActivo ? 'true' : 'false';
  if (filtros.adId) query.adId = filtros.adId;
  if (filtros.campaignId) query.campaignId = filtros.campaignId;

  // `buscar` vacío no se manda: el backend lo rechaza (`min(1)`).
  const buscar = filtros.buscar?.trim();
  if (buscar) query.buscar = buscar;
  if (filtros.desde) query.desde = filtros.desde;
  if (filtros.hasta) query.hasta = filtros.hasta;
  if (filtros.orden) query.orden = filtros.orden;
  if (filtros.limite !== undefined) query.limite = filtros.limite;
  if (filtros.offset !== undefined) query.offset = filtros.offset;

  return query;
}

/** `GET /api/tenants/:tenantId/conversations` — bandeja paginada (30 por defecto, 100 máximo). */
export function listConversations(
  tenantId: string,
  filtros: ConversationFiltros = {},
): Promise<ListaConversaciones> {
  return api.get<ListaConversaciones>(`/api/tenants/${tenantId}/conversations`, {
    query: conversationQueryFrom(filtros),
  });
}

/**
 * `GET /api/tenants/:tenantId/conversations/:conversationId` — tarjeta del inbox + sus últimas 5
 * órdenes (404 si la conversación es de otro negocio).
 */
export function fetchConversation(
  tenantId: string,
  conversationId: string,
): Promise<DetalleConversacion> {
  return api.get<DetalleConversacion>(
    `/api/tenants/${tenantId}/conversations/${conversationId}`,
  );
}

/**
 * `GET /api/tenants/:tenantId/conversations/:conversationId/messages` — una página del historial.
 *
 * Devuelve los mensajes **ascendentes** de la página pedida y el cursor para seguir hacia atrás;
 * sin `antes`/`antesId` trae los últimos `limite` (lo que se pinta al abrir el chat).
 */
export function listConversationMessages(
  tenantId: string,
  conversationId: string,
  params: ListMessagesParams = {},
): Promise<ListaMensajes> {
  const query: QueryParams = {};
  if (params.limite !== undefined) query.limite = params.limite;
  if (params.antes) query.antes = params.antes;
  if (params.antesId) query.antesId = params.antesId;
  if (params.direccion) query.direccion = params.direccion;
  if (params.tipo) query.tipo = params.tipo;

  return api.get<ListaMensajes>(
    `/api/tenants/${tenantId}/conversations/${conversationId}/messages`,
    { query },
  );
}

/**
 * `POST /api/tenants/:tenantId/conversations/:conversationId/messages` — responde a mano.
 *
 * Es la única escritura del inbox que no es el handoff y la puede usar cualquier miembro
 * (`ADMIN` y `AGENTE_HUMANO`): el backend envía el texto por el canal del contacto y lo guarda
 * como saliente, así que **descuenta la ventana de 24 h de Meta** (dentro de ella Meta no cobra:
 * desde el 1-jul-2025 factura solo las plantillas, ver `docs/PRODUCCION_META.md` §5).
 *
 * Devuelve el mismo `MensajeChat` del historial (`201`), listo para pintarlo en el hilo. Si el
 * canal no pudo entregarlo, el backend responde **502** con el motivo (el caso típico: pasaron más
 * de 24 h desde el último mensaje del cliente y hay que usar una plantilla) y **no** guarda nada.
 */
export function sendConversationMessage(
  tenantId: string,
  conversationId: string,
  contenido: string,
): Promise<MensajeChat> {
  const body: EnviarMensajeRequest = { contenido };

  return api.post<MensajeChat>(
    `/api/tenants/${tenantId}/conversations/${conversationId}/messages`,
    body,
  );
}

/**
 * REENVÍA un saliente que el canal rechazó (o cuya entrega quedó en duda) — módulo 5.
 *
 * Sin body: el contenido y el tipo salen de la fila del historial, así que el panel solo manda el id.
 * Es la contraparte del aviso «no se entregó» de la burbuja: hasta ahora el asesor veía un mensaje
 * pintado como enviado que el cliente nunca recibió y no tenía forma de reintentarlo.
 *
 * Devuelve el `MensajeChat` actualizado (`entregado: true`) para refrescar la fila en caché. Los
 * errores del backend son elocuentes y se pintan tal cual:
 *  - **502** si el canal lo vuelve a rechazar (el caso típico: la ventana de 24 h, con la sugerencia de
 *    usar una plantilla aprobada);
 *  - **409** si ya estaba entregado (reenviarlo duplicaría el mensaje en el celular del cliente);
 *  - **400** si ese tipo no se reenvía (una plantilla se manda desde Plantillas).
 */
export function resendConversationMessage(
  tenantId: string,
  conversationId: string,
  messageId: string,
): Promise<MensajeChat> {
  return api.post<MensajeChat>(
    `/api/tenants/${tenantId}/conversations/${conversationId}/messages/${messageId}/reenviar`,
  );
}

/**
 * `POST /api/tenants/:tenantId/conversations/:conversationId/fotos` — manda una FOTO a mano.
 *
 * Mismo contrato que el texto (cualquier miembro; `201` con el `MensajeChat` resultante, ya con
 * `tipo: 'IMAGEN'` y la URL en `contenido`) y tres diferencias que importan:
 *  - el binario viaja en **base64 dentro del JSON** (hasta ~2 MB; el panel reescala a 1600 px antes),
 *    y `caption` es el texto que había escrito el asesor (el cliente lo ve como pie de foto);
 *  - el backend necesita `PUBLIC_BASE_URL` para que WhatsApp pueda **descargar** la imagen: sin ella
 *    responde **400**;
 *  - si el canal no entrega la foto, responde **502** y **borra el archivo** (no queda nada en el
 *    historial ni en disco).
 */
export function sendConversationImage(
  tenantId: string,
  conversationId: string,
  imagen: EnviarImagenRequest,
): Promise<MensajeChat> {
  return api.post<MensajeChat>(
    `/api/tenants/${tenantId}/conversations/${conversationId}/fotos`,
    imagen,
  );
}

/**
 * `DELETE /api/tenants/:tenantId/conversations/:conversationId` — borrar el chat (solo ADMIN).
 *
 * Es la forma de **limpiar la memoria del bot**: se van la conversación y todos sus mensajes, y la
 * próxima vez que ese número escriba, el webhook crea una conversación nueva y vacía (el bot no
 * recuerda lo que se había quedado escrito ahí).
 *
 * `permitirConOrdenes` es la segunda confirmación: sin ella, una conversación que ya tiene ventas
 * responde **409** (las órdenes se conservan y quedarían sin su chat). Cuando el admin lo confirma en
 * el panel, la llamada se repite con el flag y ahí sí se borra.
 */
export function deleteConversation(
  tenantId: string,
  conversationId: string,
  opciones: { permitirConOrdenes?: boolean } = {},
): Promise<BorradoConversacionResultado> {
  return api.delete<BorradoConversacionResultado>(
    `/api/tenants/${tenantId}/conversations/${conversationId}`,
    // Sin el flag no se manda nada en la query: el backend lo trata como borrado normal.
    { query: opciones.permitirConOrdenes ? { permitirConOrdenes: 'true' } : undefined },
  );
}